import { Mediator, app, payload, eData } from "modular-openscriptjs";
import {
	eParse,
	inform,
	processStart,
	processEnd,
	approved,
	empty,
} from "../helpers";
import { analyze } from "../analytics";
import { STRINGS, api, uc, dc, gc } from "../declarations";
import { routes } from "../backendRoutes";

const $e = app("$e");

export class Product extends Mediator {
	$$product = {
		needs: {
			storage: (ed, event) => {
				this.saveProduct(ed, event);
			},

			creation: (ed, event) => {
				this.send($e.product.needs.productForm, ed);
			},

			allItems: () => this.getProducts(),

			deletion: (ed, event) => {
				this.deleteProduct(ed, event);
			},

			update: (ed, event) => {
				this.send($e.product.needs.productForm, ed);
			},

			share: (ed, event) => {
				this.shareProduct(ed, event);
			},

			save: (ed, event) => {
				this.copyExternalProduct(ed, event);
			},

			like: (ed, event) => {
				this.likeProduct(ed, event);
			},

			unlike: (ed, event) => {
				this.unlikeProduct(ed, event);
			},

			bookmark: (ed, event) => {
				this.bookmark(ed, event);
			},

			bookmarkRemoved: (ed, event) => {
				this.removeBookmark(ed, event);
			},

			comment: (ed, event) => this.send($e.product.needs.commentForm, ed),

			to: {
				saveComment: (ed, event) => this.makeComment(ed, event),
			},
		},
		stored: () => this.getProducts(),

		is: {
			liked_unliked_bookmarked_notBookmarked_commented: (ed, event) => {
				let product = eParse(ed).message.get("product");

				if (uc.productList[`p-${product.id}`]) {
					uc.productList[`p-${product.id}`].value = product;
				}

				switch (event) {
					case $e.product.is.notBookmarked: {
						if (
							app("router").is("user.view-profile") &&
							app("router").params.user == "current"
						) {
							uc.productList[`p-${product.id}`].value =
								null;
						}

						break;
					}
				}
			},
		},
	};

	$$user = {
		authenticated: (ed, event) => {
			this.getProducts();
		},
	};

	async saveProduct(ed, event) {
		let data = eParse(ed);

		let productInfo = data.message.getAll();
		let optional = {
			description: true,
			original_product_id: true,
			discounted_price: true,
		};

		for (let key in productInfo) {
			if (
				productInfo[key]?.length > 0 ||
				key in optional ||
				typeof productInfo[key] in { boolean: true, number: true }
			) {
				continue;
			}

			inform(`Product ${key} is required`, STRINGS.ERROR);

			return this.send($e.product.has.storageError, ed);
		}

		let path = routes.customerProducts;
		let method = "post";
		if (productInfo?.id - 0 !== 0)
			(path += `/${productInfo.id}`), (method = "put");

		let resData = await api[method](path, productInfo);
		if (!resData) return this.send($e.product.has.storageError, ed);

		let product = resData.data.product;

		let aEvent = "add_product";

		if (method == "put") {
			aEvent = "edit_product";
		}

		analyze(aEvent, {
			product_id: product.id,
			product_name: product.name,
		});

		if (dc.currentItemImages) {
			if (productInfo["original_product_id"]) {
				await api.put(
					`${routes.customerProducts}/${product.id}/images/copy`,
					{ images: dc.currentItemImages }
				);
			} else {
				await api.put(
					`${routes.customerProducts}/${product.id}/images/sync`,
					{ images: dc.currentItemImages }
				);
			}

			delete dc.currentItemImages;
		}

		if (!empty(dc.newItemImages)) {
			for (let item of dc.newItemImages) {
				let formData = new FormData();
				formData.append("image", item.image);
				formData.append("crop_using", JSON.stringify(item.cropUsing));

				// use capacitor http with api token

				await fetch(
					`${routes.customerProducts}/${product.id}/image/upload`,
					{
						headers: {
							"X-CSRF-TOKEN": api.csrf,
						},
						method: "post",
						body: formData,
					}
				);
			}

			delete dc.newItemImages;
		}

		inform(resData.message, STRINGS.SUCCESS);
		this.send(
			$e.product.stored,
			eData(data.meta, {
				product: resData.data.product,
				quantity: productInfo.quantity,
				action: aEvent,
			})
		);

		if (
			aEvent == "edit_product" &&
			uc.productList[`p-${resData.data.product.id}`]
		) {
			uc.productList[`p-${resData.data.product.id}`].value =
				resData.data.product;
		}

		app("router").to("product.view-product", {
			productId: resData.data.product?.id,
			u: gc.user?.id,
		});
	}

	async deleteProduct(ed, __event) {
		if (!approved(__event, ed, "You want to delete this product")) return;

		let data = eParse(ed);

		let res = await api.delete(
			`${routes.customerProducts}/${data.message.id}`
		);

		if (!res) return this.broadcast($e.product.has.deletionError, ed);

		inform(res.message, STRINGS.SUCCESS);

		if (`p-${res.data.product.id}` in uc.productList) {
			uc.productList[`p-${res.data.product.id}`].value = null;
		}

		data.message.put("product", res.data.product);

		return this.broadcast($e.product.deleted, data.encode());
	}

	getProducts() {
		return this;
	}

	shareProduct(ed, event) {
		let { message } = eParse(ed);

		analyze("share", {
			method: "WhatsApp",
			content_type: "product",
			item_id: message.get("id"),
		});

		let product = message.get("product");
		let link = `${window.location.origin}/app/products/${product.id}/view`;

		let text = `Hey, I have this product on my shopping list on Carata.
You can add it to your list as well if it's something you order too.

*Product: ${product.name}*

_Let your fingers do the walking._

*link:* ${link}
`;
		let url = `https://api.whatsapp.com/send?text=${encodeURI(text)}`;

		let a = h.a({ href: url, target: "_blank" });
		a.click();
	}

	async copyExternalProduct(ed, event) {
		if (
			!approved(
				event,
				ed,
				"You want to save this product to your shopping list"
			)
		) {
			return;
		}

		let { message } = eParse(ed);

		processStart({ title: "Adding product to your inventory" });
		let res = await api.post(
			`${routes.customerProducts}/save-external-product`,
			message
		);
		processEnd();

		if (!res) return;

		inform("Product Added to your inventory successfully", STRINGS.SUCCESS);

		analyze("save_external_product", { item_name: res.data.product.name });
	}

	async likeProduct(ed, event) {
		let data = eParse(ed);
		let product = data.message.get("product", null);

		let res = await api.post(
			`${routes.customerProducts}/${product?.id}/like`
		);

		if (!res) {
			return this.send($e.product.has.likeError, ed);
		}

		data.message.product.like_count++;
		data.message.product.current_like = [res.data.like];

		return this.send($e.product.is.liked, data.encode());
	}

	async unlikeProduct(ed, event) {
		let data = eParse(ed);
		let product = data.message.get("product", null);

		let res = await api.delete(
			`${routes.customerProducts}/${product?.id}/like`
		);

		if (!res) {
			return this.send($e.product.has.likeError, ed);
		}

		data.message.product.like_count--;
		data.message.product.current_like = [];

		return this.send($e.product.is.unliked, data.encode());
	}

	async bookmark(ed, event) {
		let data = eParse(ed);
		let product = data.message.get("product", null);

		let res = await api.post(
			`${routes.customerProducts}/${product?.id}/bookmark`
		);

		if (!res) {
			return this.send($e.product.has.bookmarkError, ed);
		}

		data.message.product.save_count++;
		data.message.product.current_bookmark = true;

		return this.send($e.product.is.bookmarked, data.encode());
	}

	async removeBookmark(ed, event) {
		let data = eParse(ed);
		let product = data.message.get("product", null);

		let res = await api.delete(
			`${routes.customerProducts}/${product?.id}/bookmark`
		);

		if (!res) {
			return this.send($e.product.has.bookmarkError, ed);
		}

		data.message.product.save_count--;
		data.message.product.current_bookmark = false;

		return this.send($e.product.is.notBookmarked, data.encode());
	}

	async makeComment(ed, event) {
		let { product, comment } = eParse(ed).message;

		let res = await api.post(
			`${routes.customerProducts}/${product.id}/comments`,
			{ content: comment }
		);

		if (!res) return this.send($e.product.has.commentError, ed);

		inform(res.message, STRINGS.SUCCESS);

		product = res.data.product;
		comment = res.data.comment;

		this.send($e.product.is.commented, payload({ product, comment }));
	}
}
